import { apiPost, apiPut } from "../client";
import { graphqlRequest } from "../graphqlClient";
import type {
	ActionMessageResponseApi,
	ActionMessageViewConnectionApi,
	ActionMessageViewFilterInput,
	ActionResponseApi,
	ActionViewConnectionApi,
	ActionViewFilterInput,
	ActionViewTotalsApi,
	ArchiveActionRequestApi,
	CompleteActionRequestApi,
	CreateActionMessageRequestApi,
	CreateActionRequestApi,
	ReassignActionRequestApi,
	UpdateActionRequestApi,
} from "../models/action";

// ── GraphQL fragments ─────────────────────────────────────────────────────────

const ACTION_VIEW_FIELDS = `
  id
  title
  description
  assigneeId
  assigneeName
  authorId
  authorName
  dueDate
  status
  statusDate
  statusUserId
  statusComment
  dateCreated
  dateModified
  tag
  dueDateLabel
  messageCount
`;

const ACTION_MESSAGE_VIEW_FIELDS = `
  id
  actionId
  authorId
  authorName
  recipientId
  recipientName
  message
  dateCreated
`;

// ── GraphQL queries (reads) ───────────────────────────────────────────────────

interface ActionViewListResponse {
	actions: {
		actionViewList: ActionViewConnectionApi;
	};
}

interface ActionViewTotalsResponse {
	actions: {
		actionViewTotals: ActionViewTotalsApi;
	};
}

interface ActionViewCountResponse {
	actions: {
		actionViewCount: number;
	};
}

interface ActionMessageViewListResponse {
	actions: {
		actionMessageViewList: ActionMessageViewConnectionApi;
	};
}

export async function getActionViewList(
	filter?: ActionViewFilterInput,
	first = 100,
): Promise<ActionViewConnectionApi> {
	const query = `
    query ActionViewList($filter: ActionViewFilterInput, $first: Int) {
      actions {
        actionViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {
              ${ACTION_VIEW_FIELDS}
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;

	const data = await graphqlRequest<ActionViewListResponse>(query, {
		filter,
		first,
	});
	return data.actions.actionViewList;
}

export async function getActionViewTotals(
	filter?: ActionViewFilterInput,
): Promise<ActionViewTotalsApi> {
	const query = `
    query ActionViewTotals($filter: ActionViewFilterInput) {
      actions {
        actionViewTotals(filter: $filter) {
          totalCount
          openCount
          completeCount
          archivedCount
          overdueCount
        }
      }
    }
  `;

	const data = await graphqlRequest<ActionViewTotalsResponse>(query, {
		filter,
	});
	return data.actions.actionViewTotals;
}

export async function getActionViewCount(
	filter?: ActionViewFilterInput,
): Promise<number> {
	const query = `
    query ActionViewCount($filter: ActionViewFilterInput) {
      actions {
        actionViewCount(filter: $filter)
      }
    }
  `;

	const data = await graphqlRequest<ActionViewCountResponse>(query, {
		filter,
	});
	return data.actions.actionViewCount;
}

export async function getActionMessageViewList(
	filter?: ActionMessageViewFilterInput,
	first = 100,
): Promise<ActionMessageViewConnectionApi> {
	const query = `
    query ActionMessageViewList($filter: ActionMessageViewFilterInput, $first: Int) {
      actions {
        actionMessageViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {
              ${ACTION_MESSAGE_VIEW_FIELDS}
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;

	const data = await graphqlRequest<ActionMessageViewListResponse>(query, {
		filter,
		first,
	});
	return data.actions.actionMessageViewList;
}

// ── REST mutations (writes) ───────────────────────────────────────────────────

export async function createAction(
	request: CreateActionRequestApi,
): Promise<ActionResponseApi> {
	return apiPost<ActionResponseApi>("actions", request);
}

export async function updateAction(
	actionId: number,
	request: UpdateActionRequestApi,
): Promise<ActionResponseApi> {
	return apiPut<ActionResponseApi>(`actions/${actionId}`, request);
}

export async function completeAction(
	actionId: number,
	request: CompleteActionRequestApi,
): Promise<ActionResponseApi> {
	return apiPost<ActionResponseApi>(`actions/${actionId}/complete`, request);
}

export async function archiveAction(
	actionId: number,
	request?: ArchiveActionRequestApi,
): Promise<ActionResponseApi> {
	return apiPost<ActionResponseApi>(
		`actions/${actionId}/archive`,
		request ?? {},
	);
}

export async function reopenAction(
	actionId: number,
): Promise<ActionResponseApi> {
	return apiPost<ActionResponseApi>(`actions/${actionId}/reopen`);
}

export async function reassignAction(
	actionId: number,
	request: ReassignActionRequestApi,
): Promise<ActionResponseApi> {
	return apiPost<ActionResponseApi>(`actions/${actionId}/reassign`, request);
}

export async function addActionMessage(
	actionId: number,
	request: CreateActionMessageRequestApi,
): Promise<ActionMessageResponseApi> {
	return apiPost<ActionMessageResponseApi>(
		`actions/${actionId}/messages`,
		request,
	);
}
